import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TableModule, Table } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { BadgeModule } from 'primeng/badge';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { TasksService } from '../../_services/tasks/tasks.service';
import { SessionService } from '../../_services/session/session.service';
import { SitesService } from '../../_services/sites/sites.service';
import { BaseComponent } from '../../components/base/base.component';
import { KpiCardComponent } from '../statistics/_components/kpi-card/kpi-card.component';
import {
  ITaskNew,
  ITaskStats,
  TaskPriority,
  TaskStatus,
} from '../../_interfaces/ITask';
import IUser from '../../_interfaces/IUser';

interface FilterOption {
  label: string;
  value: string | null;
}

interface UserFilterOption {
  label: string;
  value: number | null;
}

interface SortOption {
  label: string;
  value: string;
}

// Estructura para agrupar tareas con subtareas
interface TaskGroup {
  task: ITaskNew;
  subtasks: ITaskNew[];
  isExpanded: boolean;
}

@Component({
  standalone: true,
  selector: 'app-tasks-real',
  templateUrl: './tasks-real.component.html',
  styleUrls: ['./tasks-real.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
    ToastModule,
    TableModule,
    DropdownModule,
    InputTextModule,
    CalendarModule,
    MultiSelectModule,
    TagModule,
    IconFieldModule,
    InputIconModule,
    CheckboxModule,
    ConfirmDialogModule,
    BadgeModule,
    TranslateModule,
    KpiCardComponent,
  ],
  providers: [MessageService, ConfirmationService],
})
export class TasksRealComponent
  extends BaseComponent
  implements OnInit, OnDestroy {
  @ViewChild('tasksTable') tasksTable!: Table;

  private destroy$ = new Subject<void>();

  // Datos de tareas
  allTasks: ITaskNew[] = [];
  filteredTasks: ITaskNew[] = [];

  // Agrupación de tareas padre con subtareas
  taskGroups: TaskGroup[] = [];
  flatTasksForTable: {
    task: ITaskNew;
    isSubtask: boolean;
    parentIndex: number;
  }[] = [];
  orphanSubtasks: ITaskNew[] = [];

  // Estado de la vista
  loading: boolean = true;

  // Mostrar/ocultar estadísticas compactas
  showStats: boolean = true;

  // Estadísticas
  stats: ITaskStats = {
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    overdue: 0,
  };

  // Tareas que vencen pronto (para alertas)
  dueSoonTasks: ITaskNew[] = [];
  showDueSoonAlert: boolean = false;

  // Usuarios para filtros
  users: IUser[] = [];
  userOptions: UserFilterOption[] = [];

  // Es admin - puede ver todas las tareas
  isAdmin: boolean = false;

  // Filtros
  globalFilter: string = '';

  selectedStatuses: string[] = [];

  selectedQuickFilter: string = 'all';

  selectedPriorities: string[] = [];

  selectedSort: string = '';

  selectedAssignee: number | null = null;
  selectedCreator: number | null = null;

  dueDateFrom: Date | null = null;
  dueDateTo: Date | null = null;

  showOnlyOverdue: boolean = false;

  showAdvancedFilters: boolean = false;

  // Opciones de filtro rápido
  statusOptions: FilterOption[] = [];

  priorityOptions: FilterOption[] = [];

  quickFilterOptions: FilterOption[] = [];

  sortOptions: SortOption[] = [];

  constructor(
    private tasksService: TasksService,
    private sitesService: SitesService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public session: SessionService,
    private translate: TranslateService,
  ) {
    super(session);
  }

  get canCreateTasks(): boolean {
    return this.canAccessModule('tasks', {
      anyPermission: ['TASKS.CREATE'],
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initFilterOptions();
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.initFilterOptions());
    this.checkAdminRole();
    this.loadUsers();
    this.loadTasks();
    this.loadDueSoonTasks();

    // Verificar si hay query params para creación (redirigir a la vista)
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (params['createTask'] === 'true') {
          if (!this.canCreateTasks) {
            return;
          }
          const patientId = params['patientId'] || '';
          const appointmentId = params['appointmentId'] || '';
          const customer = this.session.getCustomer();
          const site = this.session.getSite();
          if (customer && site) {
            this.router.navigate(
              [`/${customer.identifier}/${site.identifier}/tasks/new`],
              {
                queryParams: { patientId, appointmentId },
              },
            );
          }
        }
      });
  }

  private initFilterOptions(): void {
    this.statusOptions = [
      {
        label: this.translate.instant('TASKS.STATUS.PENDING'),
        value: 'pending',
      },
      {
        label: this.translate.instant('TASKS.STATUS.IN_PROGRESS'),
        value: 'in-progress',
      },
      {
        label: this.translate.instant('TASKS.STATUS.COMPLETED'),
        value: 'completed',
      },
      {
        label: this.translate.instant('TASKS.STATUS.CANCELLED'),
        value: 'cancelled',
      },
    ];

    this.priorityOptions = [
      {
        label: this.translate.instant('TASKS.PRIORITY.URGENT'),
        value: 'urgent',
      },
      { label: this.translate.instant('TASKS.PRIORITY.HIGH'), value: 'high' },
      {
        label: this.translate.instant('TASKS.PRIORITY.MEDIUM'),
        value: 'medium',
      },
      { label: this.translate.instant('TASKS.PRIORITY.LOW'), value: 'low' },
    ];

    this.quickFilterOptions = [
      {
        label: this.translate.instant('TASKS.UI.FILTERS.QUICK.ALL'),
        value: 'all',
      },
      {
        label: this.translate.instant('TASKS.UI.FILTERS.QUICK.PENDING'),
        value: 'pending',
      },
      {
        label: this.translate.instant('TASKS.UI.FILTERS.QUICK.IN_PROGRESS'),
        value: 'in-progress',
      },
      {
        label: this.translate.instant('TASKS.UI.FILTERS.QUICK.OVERDUE'),
        value: 'overdue',
      },
      {
        label: this.translate.instant('TASKS.UI.FILTERS.QUICK.DUE_SOON'),
        value: 'due_soon',
      },
      {
        label: this.translate.instant('TASKS.UI.FILTERS.QUICK.COMPLETED'),
        value: 'completed',
      },
    ];

    this.sortOptions = [
      {
        label: this.translate.instant('TASKS.UI.SORT.CREATED_DESC'),
        value: 'createdAt_desc',
      },
      {
        label: this.translate.instant('TASKS.UI.SORT.CREATED_ASC'),
        value: 'createdAt_asc',
      },
      {
        label: this.translate.instant('TASKS.UI.SORT.DUE_ASC'),
        value: 'due_date_asc',
      },
      {
        label: this.translate.instant('TASKS.UI.SORT.DUE_DESC'),
        value: 'due_date_desc',
      },
      {
        label: this.translate.instant('TASKS.UI.SORT.PRIORITY_DESC'),
        value: 'priority_desc',
      },
      {
        label: this.translate.instant('TASKS.UI.SORT.PRIORITY_ASC'),
        value: 'priority_asc',
      },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Verificar si el usuario tiene rol de admin
   */
  checkAdminRole(): void {
    this.isAdmin = this.isSiteAdmin() || this.isSuperUser();
  }

  private isSiteAdmin(): boolean {
    const customer = this.session.getCustomer();
    return customer
      ? this.session.isCustomerAdmin(customer)
      : this.session.isCustomerAdmin();
  }

  loadUsers(): void {
    this.sitesService
      .getUsers(this.customers_id, this.sites_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users) => {
          this.users = users;
          this.userOptions = [
            { label: 'Todos', value: null },
            ...users.map((u) => ({
              label: `${u.name} ${u.surname || ''}`.trim(),
              value: u.id!,
            })),
          ];
        },
        error: (error) => console.error('Error loading users:', error),
      });
  }

  /**
   * Cargar tareas próximas a vencer para mostrar alertas
   */
  loadDueSoonTasks(): void {
    if (!this.customers_id || !this.sites_id) return;

    this.tasksService
      .getDueSoonTasks(this.customers_id, this.sites_id, { days: 3, limit: 10 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dueSoonTasks = response.data;
          this.showDueSoonAlert = this.dueSoonTasks.length > 0;
        },
        error: (error) => console.error('Error loading due soon tasks:', error),
      });
  }

  loadTasks(): void {
    this.loading = true;

    // Si no hay IDs válidos, mostrar página vacía
    if (!this.customers_id || !this.sites_id) {
      console.warn('TasksReal: No customers_id or sites_id available', {
        customers_id: this.customers_id,
        sites_id: this.sites_id,
      });
      this.allTasks = [];
      this.stats = {
        total: 0,
        pending: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
        overdue: 0,
      };
      this.applyFilters();
      this.loading = false;
      return;
    }

    // Si es admin, cargar todas las tareas; si no, solo las asignadas/creadas por el usuario
    if (this.isAdmin) {
      this.loadAllTasks();
    } else {
      this.loadUserTasks();
    }
  }

  /**
   * Cargar todas las tareas (para admins)
   */
  private loadAllTasks(): void {
    forkJoin({
      tasks: this.tasksService.getTasks(this.customers_id, this.sites_id, {
        limit: 1000,
      }),
      stats: this.tasksService
        .getTaskStats(this.customers_id, this.sites_id)
        .pipe(
          catchError((err) => {
            console.error('Error loading stats:', err);
            return of({
              total: 0,
              pending: 0,
              inProgress: 0,
              completed: 0,
              cancelled: 0,
              overdue: 0,
            });
          }),
        ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ tasks, stats }) => {
          this.allTasks = tasks.data;
          this.stats = stats;
          this.applyFilters();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading tasks:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
            detail: this.getErrorDetail(
              error,
              'TASKS.MESSAGES.LOAD_LIST_ERROR',
            ),
          });
          this.loading = false;
        },
      });
  }

  /**
   * Cargar solo tareas del usuario (asignadas o creadas)
   */
  private loadUserTasks(): void {
    forkJoin({
      myTasks: this.tasksService.getMyTasks(this.customers_id, this.sites_id, {
        limit: 1000,
      }),
      createdByMe: this.tasksService.getCreatedByMe(
        this.customers_id,
        this.sites_id,
        { limit: 1000 },
      ),
      stats: this.tasksService
        .getTaskStats(this.customers_id, this.sites_id)
        .pipe(
          catchError((err) => {
            console.error('Error loading stats:', err);
            return of({
              total: 0,
              pending: 0,
              inProgress: 0,
              completed: 0,
              cancelled: 0,
              overdue: 0,
            });
          }),
        ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ myTasks, createdByMe, stats }) => {
          // Combinar tareas asignadas a mí + creadas por mí (sin duplicados)
          const allTasksMap = new Map<number, ITaskNew>();
          myTasks.data.forEach((t) => allTasksMap.set(t.id, t));
          createdByMe.data.forEach((t) => allTasksMap.set(t.id, t));

          this.allTasks = Array.from(allTasksMap.values());
          this.stats = this.computeStats(myTasks.data);
          this.applyFilters();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading tasks:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
            detail: this.getErrorDetail(
              error,
              'TASKS.MESSAGES.LOAD_LIST_ERROR',
            ),
          });
          this.loading = false;
        },
      });
  }

  private computeStats(tasks: ITaskNew[]): ITaskStats {
    const now = new Date();
    const stats: ITaskStats = {
      total: tasks.length,
      pending: 0,
      inProgress: 0,
      completed: 0,
      cancelled: 0,
      overdue: 0,
    };

    tasks.forEach((task) => {
      switch (task.status) {
        case 'pending':
          stats.pending++;
          break;
        case 'in-progress':
          stats.inProgress++;
          break;
        case 'completed':
          stats.completed++;
          break;
        case 'cancelled':
          stats.cancelled++;
          break;
        default:
          break;
      }

      const dueDate = task.due_date ? new Date(task.due_date) : null;
      if (
        dueDate &&
        dueDate < now &&
        task.status !== 'completed' &&
        task.status !== 'cancelled'
      ) {
        stats.overdue++;
      }
    });

    return stats;
  }

  applyFilters(): void {
    let tasks = [...this.allTasks];
    const now = new Date();
    const currentUserId = this.user?.id;

    // Filtro rápido
    switch (this.selectedQuickFilter) {
      case 'pending':
        tasks = tasks.filter((t) => t.status === 'pending');
        break;
      case 'in-progress':
        tasks = tasks.filter((t) => t.status === 'in-progress');
        break;
      case 'assigned_to_me':
        tasks = tasks.filter((t) => this.isAssignedToUser(t, currentUserId));
        break;
      case 'created_by_me':
        tasks = tasks.filter((t) => t.created_by === currentUserId);
        break;
      case 'overdue':
        tasks = tasks.filter(
          (t) =>
            t.status !== 'completed' &&
            t.status !== 'cancelled' &&
            new Date(t.due_date) < now,
        );
        break;
      case 'due_soon':
        const in72Hours = new Date(now.getTime() + 72 * 60 * 60 * 1000);
        tasks = tasks.filter(
          (t) =>
            t.status !== 'completed' &&
            t.status !== 'cancelled' &&
            new Date(t.due_date) >= now &&
            new Date(t.due_date) <= in72Hours,
        );
        break;
      case 'not_completed':
        tasks = tasks.filter(
          (t) => t.status !== 'completed' && t.status !== 'cancelled',
        );
        break;
      case 'completed':
        tasks = tasks.filter((t) => t.status === 'completed');
        break;
      default:
        break;
    }

    // Filtro por estados
    if (this.selectedStatuses.length > 0) {
      tasks = tasks.filter((t) => this.selectedStatuses.includes(t.status));
    }

    // Filtro por prioridades
    if (this.selectedPriorities.length > 0) {
      tasks = tasks.filter((t) => this.selectedPriorities.includes(t.priority));
    }

    // Filtro por asignado
    if (this.selectedAssignee !== null) {
      tasks = tasks.filter((t) =>
        this.isAssignedToUser(t, this.selectedAssignee!),
      );
    }

    // Filtro por creador
    if (this.selectedCreator !== null) {
      tasks = tasks.filter((t) => t.created_by === this.selectedCreator);
    }

    // Filtro por rango de fechas
    if (this.dueDateFrom) {
      const fromDate = new Date(this.dueDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      tasks = tasks.filter((t) => new Date(t.due_date) >= fromDate);
    }
    if (this.dueDateTo) {
      const toDate = new Date(this.dueDateTo);
      toDate.setHours(23, 59, 59, 999);
      tasks = tasks.filter((t) => new Date(t.due_date) <= toDate);
    }

    // Filtro solo vencidas
    if (this.showOnlyOverdue) {
      tasks = tasks.filter(
        (t) =>
          t.status !== 'completed' &&
          t.status !== 'cancelled' &&
          new Date(t.due_date) < now,
      );
    }

    // Búsqueda global
    if (this.globalFilter.trim()) {
      const search = this.globalFilter.toLowerCase().trim();
      tasks = tasks.filter(
        (t) =>
          t.title.toLowerCase().includes(search) ||
          (t.description && t.description.toLowerCase().includes(search)) ||
          (t.task_key && t.task_key.toLowerCase().includes(search)) ||
          this.getAssigneeNames(t).toLowerCase().includes(search) ||
          (t.patients &&
            t.patients.some((p) =>
              `${p.name} ${p.surname || ''}`.toLowerCase().includes(search),
            )),
      );
    }

    // Aplicar ordenamiento
    tasks = this.sortTasks(tasks);

    this.filteredTasks = tasks;

    // Agrupar tareas padre con subtareas
    this.buildTaskGroups();
  }

  /**
   * Verificar si una tarea está asignada a un usuario (soporta múltiples asignados)
   */
  isAssignedToUser(task: ITaskNew, userId: number | null | undefined): boolean {
    if (!userId) return false;
    // Verificar en assigned_to (legacy)
    if (task.assigned_to === userId) return true;
    // Verificar en assignees (múltiples)
    if (task.assignees && task.assignees.some((a) => a.id === userId))
      return true;
    return false;
  }

  /**
   * Ordenar tareas según la opción seleccionada
   */
  sortTasks(tasks: ITaskNew[]): ITaskNew[] {
    const [field, order] = this.selectedSort.split('_');
    const direction = order === 'desc' ? -1 : 1;

    const priorityOrder: Record<TaskPriority, number> = {
      urgent: 4,
      high: 3,
      medium: 2,
      low: 1,
    };

    return tasks.sort((a, b) => {
      switch (field) {
        case 'createdAt':
          return (
            (new Date(a.createdAt).getTime() -
              new Date(b.createdAt).getTime()) *
            direction
          );
        case 'due':
          return (
            (new Date(a.due_date).getTime() - new Date(b.due_date).getTime()) *
            direction
          );
        case 'priority':
          return (
            (priorityOrder[a.priority] - priorityOrder[b.priority]) * direction
          );
        default:
          return 0;
      }
    });
  }

  /**
   * Construir grupos de tareas padre con sus subtareas
   */
  buildTaskGroups(): void {
    // Separar tareas padre (sin parent_tasks_id) y subtareas
    const parentTasks = this.filteredTasks.filter((t) => !t.parent_tasks_id);
    const subtasksMap = new Map<number, ITaskNew[]>();
    const parentIds = new Set(parentTasks.map((t) => t.id));
    const allSubtasks = this.filteredTasks.filter((t) => t.parent_tasks_id);

    // Agrupar subtareas por parent_tasks_id
    allSubtasks.forEach((subtask) => {
      const parentId = subtask.parent_tasks_id!;
      if (!subtasksMap.has(parentId)) {
        subtasksMap.set(parentId, []);
      }
      subtasksMap.get(parentId)!.push(subtask);
    });

    // Subtareas cuyo padre no está en la lista filtrada
    this.orphanSubtasks = allSubtasks.filter(
      (subtask) => !parentIds.has(subtask.parent_tasks_id!),
    );

    // Crear grupos
    this.taskGroups = parentTasks.map((task) => ({
      task,
      subtasks: subtasksMap.get(task.id) || [],
      isExpanded: task._expanded || false,
    }));

    // Construir lista plana para la tabla
    this.buildFlatTaskList();
  }

  /**
   * Construir lista plana de tareas para renderizar en la tabla
   */
  buildFlatTaskList(): void {
    this.flatTasksForTable = [];

    this.taskGroups.forEach((group, index) => {
      // Agregar tarea padre
      this.flatTasksForTable.push({
        task: group.task,
        isSubtask: false,
        parentIndex: index,
      });

      // Si está expandido, agregar subtareas
      if (group.isExpanded) {
        group.subtasks.forEach((subtask) => {
          this.flatTasksForTable.push({
            task: subtask,
            isSubtask: true,
            parentIndex: index,
          });
        });
      }
    });

    // Agregar subtareas sin padre visible
    if (this.orphanSubtasks.length > 0) {
      this.orphanSubtasks.forEach((subtask) => {
        this.flatTasksForTable.push({
          task: subtask,
          isSubtask: true,
          parentIndex: -1,
        });
      });
    }
  }

  /**
   * Toggle expandir/colapsar subtareas de una tarea padre
   */
  toggleSubtasks(taskId: number): void {
    const group = this.taskGroups.find((g) => g.task.id === taskId);
    if (group) {
      group.isExpanded = !group.isExpanded;
      group.task._expanded = group.isExpanded;
      this.buildFlatTaskList();
    }
  }

  /**
   * Verificar si una tarea tiene subtareas
   */
  hasSubtasks(taskId: number): boolean {
    const group = this.taskGroups.find((g) => g.task.id === taskId);
    return group ? group.subtasks.length > 0 : false;
  }

  /**
   * Obtener número de subtareas
   */
  getSubtasksCount(taskId: number): number {
    const group = this.taskGroups.find((g) => g.task.id === taskId);
    return group ? group.subtasks.length : 0;
  }

  /**
   * Verificar si un grupo está expandido
   */
  isGroupExpanded(taskId: number): boolean {
    const group = this.taskGroups.find((g) => g.task.id === taskId);
    return group ? group.isExpanded : false;
  }

  /**
   * Cerrar alerta de tareas próximas a vencer
   */
  closeDueSoonAlert(): void {
    this.showDueSoonAlert = false;
  }

  private translateCode(code?: string): string {
    if (!code) return '';
    const key = `TASKS.CODES.${code}`;
    const translated = this.translate.instant(key);
    return translated === key ? code : translated;
  }

  private getErrorDetail(error: any, fallbackKey: string): string {
    const code = error?.error?.code || error?.error?.message;
    const translated = this.translateCode(code);
    if (translated) return translated;
    return this.translate.instant(fallbackKey);
  }

  onSortChange(): void {
    this.applyFilters();
  }

  onQuickFilterChange(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.globalFilter = '';
    this.selectedQuickFilter = 'all';
    this.selectedStatuses = [];
    this.selectedPriorities = [];
    this.selectedAssignee = null;
    this.selectedCreator = null;
    this.dueDateFrom = null;
    this.dueDateTo = null;
    this.showOnlyOverdue = false;
    this.applyFilters();
    if (this.tasksTable) {
      this.tasksTable.clear();
    }
  }

  hasActiveFilters(): boolean {
    return (
      this.globalFilter.trim().length > 0 ||
      this.selectedQuickFilter !== 'all' ||
      this.selectedStatuses.length > 0 ||
      this.selectedPriorities.length > 0 ||
      this.selectedAssignee !== null ||
      this.selectedCreator !== null ||
      this.dueDateFrom !== null ||
      this.dueDateTo !== null ||
      this.showOnlyOverdue
    );
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  onGlobalFilter(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.globalFilter = target.value;
    this.applyFilters();
  }

  // Helpers para la tabla
  getPriorityLabel(priority: TaskPriority): string {
    const labels: Record<TaskPriority, string> = {
      urgent: this.translate.instant('TASKS.PRIORITY.URGENT'),
      high: this.translate.instant('TASKS.PRIORITY.HIGH'),
      medium: this.translate.instant('TASKS.PRIORITY.MEDIUM'),
      low: this.translate.instant('TASKS.PRIORITY.LOW'),
    };
    return labels[priority] || priority;
  }

  getPrioritySeverity(
    priority: TaskPriority,
  ):
    | 'success'
    | 'secondary'
    | 'info'
    | 'warn'
    | 'danger'
    | 'contrast'
    | undefined {
    const severities: Record<
      TaskPriority,
      'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast'
    > = {
      urgent: 'danger',
      high: 'warn',
      medium: 'info',
      low: 'secondary',
    };
    return severities[priority];
  }

  getStatusLabel(status: TaskStatus): string {
    const labels: Record<TaskStatus, string> = {
      pending: this.translate.instant('TASKS.STATUS.PENDING'),
      'in-progress': this.translate.instant('TASKS.STATUS.IN_PROGRESS'),
      completed: this.translate.instant('TASKS.STATUS.COMPLETED'),
      cancelled: this.translate.instant('TASKS.STATUS.CANCELLED'),
    };
    return labels[status] || status;
  }

  getStatusSeverity(
    status: TaskStatus,
  ):
    | 'success'
    | 'secondary'
    | 'info'
    | 'warn'
    | 'danger'
    | 'contrast'
    | undefined {
    const severities: Record<
      TaskStatus,
      'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast'
    > = {
      pending: 'warn',
      'in-progress': 'info',
      completed: 'success',
      cancelled: 'secondary',
    };
    return severities[status];
  }

  isOverdue(task: ITaskNew): boolean {
    if (task.status === 'completed' || task.status === 'cancelled')
      return false;
    return new Date(task.due_date) < new Date();
  }

  isDueSoon(task: ITaskNew): boolean {
    if (task.status === 'completed' || task.status === 'cancelled')
      return false;
    const dueDate = new Date(task.due_date);
    const now = new Date();
    const in24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    return dueDate >= now && dueDate <= in24Hours;
  }

  getAssigneeName(task: ITaskNew): string {
    // Primero verificar si hay múltiples asignados
    if (task.assignees && task.assignees.length > 0) {
      const first = task.assignees[0];
      const name = `${first.name} ${first.surname || ''}`.trim();
      if (task.assignees.length > 1) {
        return `${name} (+${task.assignees.length - 1})`;
      }
      return name;
    }
    // Fallback al asignado único
    if (task.assignee) {
      return `${task.assignee.name} ${task.assignee.surname || ''}`.trim();
    }

    if (task.assigned_to) {
      const user = this.users.find((u) => u.id === task.assigned_to);
      if (user) {
        return `${user.name} ${user.surname || ''}`.trim();
      }
    }

    return this.translate.instant('TASKS.UI.ASSIGNEE.NONE');
  }

  getUserShortLabel(user?: {
    abbr?: string;
    name?: string;
    surname?: string;
  }): string {
    if (!user) return '??';
    if (user.abbr && user.abbr.trim().length === 2) {
      return user.abbr.toUpperCase();
    }
    const name = `${user.name || ''} ${user.surname || ''}`.trim();
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return (parts[0] || '??').substring(0, 2).toUpperCase();
  }

  /**
   * Obtener todos los nombres de asignados (para tooltips y búsqueda)
   */
  getAssigneeNames(task: ITaskNew): string {
    if (task.assignees && task.assignees.length > 0) {
      return task.assignees
        .map((a) => `${a.name} ${a.surname || ''}`.trim())
        .join(', ');
    }
    if (task.assignee) {
      return `${task.assignee.name} ${task.assignee.surname || ''}`.trim();
    }
    if (task.assigned_to) {
      const user = this.users.find((u) => u.id === task.assigned_to);
      if (user) {
        return `${user.name} ${user.surname || ''}`.trim();
      }
    }
    return this.translate.instant('TASKS.UI.ASSIGNEE.NONE');
  }

  getCreatorName(task: ITaskNew): string {
    if (!task.creator) return this.translate.instant('TASKS.UI.EMPTY_VALUE');
    return `${task.creator.name} ${task.creator.surname || ''}`.trim();
  }

  getPatientName(task: ITaskNew): string {
    if (!task.patients || task.patients.length === 0)
      return this.translate.instant('TASKS.UI.EMPTY_VALUE');
    // Retorna el nombre del primer paciente, o indica si hay más
    const firstPatient = task.patients[0];
    const name = `${firstPatient.name} ${firstPatient.surname || ''}`.trim();
    if (task.patients.length > 1) {
      return `${name} (+${task.patients.length - 1})`;
    }
    return name;
  }

  getPatientsNames(task: ITaskNew): string {
    if (!task.patients || task.patients.length === 0) {
      return this.translate.instant('TASKS.UI.EMPTY_VALUE');
    }
    return task.patients
      .map((p) => `${p.name} ${p.surname || ''}`.trim())
      .join(', ');
  }

  formatDate(date: Date | string): string {
    const d = new Date(date);
    return d.toLocaleDateString(this.getLocale(), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  formatDateTime(date: Date | string): string {
    const d = new Date(date);
    return d.toLocaleDateString(this.getLocale(), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private getLocale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang;
    switch (lang) {
      case 'es':
        return 'es-ES';
      case 'hr':
        return 'hr-HR';
      case 'en':
        return 'en-US';
      default:
        return 'en-US';
    }
  }

  // Permisos de tareas
  /**
   * Verifica si el usuario actual es el creador de la tarea
   * Solo el creador (o superusuario) puede editar/eliminar la tarea
   */
  isTaskCreator(task: ITaskNew): boolean {
    if (!task || !this.users_id) return false;
    return (
      task.created_by === this.users_id ||
      this.isSiteAdmin() ||
      this.isSuperUser()
    );
  }

  /**
   * Verifica si el usuario actual puede cambiar el estado de la tarea
   * El creador O el asignado pueden cambiar el estado
   */
  canChangeTaskStatus(task: ITaskNew): boolean {
    if (!task || !this.users_id) return false;
    return (
      task.created_by === this.users_id ||
      task.assigned_to === this.users_id ||
      (task.assignees || []).some((u) => u.id === this.users_id) ||
      this.isSiteAdmin() ||
      this.isSuperUser()
    );
  }

  canEditTask(task: ITaskNew): boolean {
    return this.isTaskCreator(task);
  }

  // Acciones
  openCreateView(): void {
    if (!this.canCreateTasks) return;
    const customer = this.session.getCustomer();
    const site = this.session.getSite();
    if (customer && site) {
      this.router.navigate([
        `/${customer.identifier}/${site.identifier}/tasks/new`,
      ]);
    }
  }

  viewTask(task: ITaskNew): void {
    const customer = this.session.getCustomer();
    const site = this.session.getSite();
    if (customer && site && task.task_key) {
      this.router.navigate(
        [`/${customer.identifier}/${site.identifier}/tasks`, task.task_key],
        { queryParams: { taskId: task.id } },
      );
    }
  }

  editTask(task: ITaskNew): void {
    const customer = this.session.getCustomer();
    const site = this.session.getSite();
    if (customer && site && task.task_key) {
      this.router.navigate(
        [`/${customer.identifier}/${site.identifier}/tasks`, task.task_key],
        { queryParams: { edit: 'true', taskId: task.id } },
      );
    }
  }

  toggleComplete(task: ITaskNew): void {
    const newStatus: TaskStatus =
      task.status === 'completed' ? 'pending' : 'completed';

    this.tasksService
      .updateTaskStatus(this.customers_id, this.sites_id, task.id, newStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadTasks();
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('TASKS.MESSAGES.SUCCESS_TITLE'),
            detail:
              newStatus === 'completed'
                ? this.translate.instant('TASKS.MESSAGES.STATUS_COMPLETED')
                : this.translate.instant('TASKS.MESSAGES.STATUS_REOPENED'),
          });
        },
        error: (error) => {
          console.error('Error updating task status:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
            detail: this.getErrorDetail(
              error,
              'TASKS.MESSAGES.STATUS_UPDATE_ERROR',
            ),
          });
        },
      });
  }

  deleteTask(task: ITaskNew): void {
    const taskName = task.title || task.task_key || '';
    this.translate
      .get(
        [
          'TASKS.CONFIRM.DELETE.MESSAGE',
          'TASKS.CONFIRM.DELETE.TITLE',
          'TASKS.CONFIRM.DELETE.ACCEPT',
          'TASKS.CONFIRM.DELETE.REJECT',
          'TASKS.MESSAGES.SUCCESS_TITLE',
          'TASKS.MESSAGES.DELETE_SUCCESS',
          'TASKS.MESSAGES.ERROR_TITLE',
          'TASKS.MESSAGES.DELETE_ERROR',
        ],
        { task: taskName },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((t) => {
        this.confirmationService.confirm({
          message: t['TASKS.CONFIRM.DELETE.MESSAGE'],
          header: t['TASKS.CONFIRM.DELETE.TITLE'],
          icon: 'fas fa-trash',
          acceptLabel: t['TASKS.CONFIRM.DELETE.ACCEPT'],
          rejectLabel: t['TASKS.CONFIRM.DELETE.REJECT'],
          acceptButtonStyleClass: 'p-button-danger',
          accept: () => {
            this.tasksService
              .deleteTask(this.customers_id, this.sites_id, task.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.loadTasks();
                  this.messageService.add({
                    severity: 'success',
                    summary: t['TASKS.MESSAGES.SUCCESS_TITLE'],
                    detail: t['TASKS.MESSAGES.DELETE_SUCCESS'],
                  });
                },
                error: (error) => {
                  console.error('Error deleting task:', error);
                  this.messageService.add({
                    severity: 'error',
                    summary: t['TASKS.MESSAGES.ERROR_TITLE'],
                    detail: this.getErrorDetail(
                      error,
                      'TASKS.MESSAGES.DELETE_ERROR',
                    ),
                  });
                },
              });
          },
        });
      });
  }

  toggleStats(): void {
    this.showStats = !this.showStats;
  }

  onCalendarTaskClick(task: ITaskNew): void {
    this.viewTask(task);
  }

  // Exportar a CSV
  exportToCSV(): void {
    const headers = [
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.TITLE'),
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.DESCRIPTION'),
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.PRIORITY'),
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.STATUS'),
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.DUE_DATE'),
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.ASSIGNED'),
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.CREATED_BY'),
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.PATIENT'),
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.CREATED_AT'),
      this.translate.instant('TASKS.UI.EXPORT.HEADERS.UPDATED_AT'),
    ];
    const rows = this.filteredTasks.map((t) => [
      t.title,
      t.description || '',
      this.getPriorityLabel(t.priority),
      this.getStatusLabel(t.status),
      this.formatDate(t.due_date),
      this.getAssigneeName(t),
      this.getCreatorName(t),
      this.getPatientName(t),
      this.formatDateTime(t.createdAt),
      this.formatDateTime(t.updatedAt),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${this.translate.instant('TASKS.UI.EXPORT.FILENAME_PREFIX')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }
}
